import { useCallback, useEffect, useState } from "react";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, PencilSimple, Trash, PaperPlaneTilt } from "@phosphor-icons/react";

const EMPTY = { email: "", name: "", role: "manager", password: "", employee_id: "" };

const ROLE_LABEL = { admin: "Amministratore", titolare: "Titolare", manager: "Responsabile", rspp: "RSPP", employee: "Dipendente" };

export default function Users() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [testing, setTesting] = useState(null);

  const testEmail = async (r) => {
    setTesting(r.id);
    try {
      const res = await api.post(`/users/${r.id}/test-email`);
      toast.success(`Email di prova inviata a ${res.data.email}`);
    } catch (e) { toast.error(errMsg(e)); } finally { setTesting(null); }
  };

  const load = useCallback(() => {
    api.get("/users").then((r) => setRows(r.data)).catch((e) => toast.error(errMsg(e)));
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get("/employees", { params: { status: "active" } }).then((r) => setEmployees(r.data)).catch(() => {});
  }, []);

  const submit = async () => {
    try {
      if (form.id) {
        const payload = { name: form.name, role: form.role, active: form.active, employee_id: form.employee_id || null, notify_email: form.notify_email || "" };
        if (form.password) payload.password = form.password;
        await api.put(`/users/${form.id}`, payload);
      } else {
        await api.post("/users", form);
      }
      toast.success("Utente salvato");
      setForm(null);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const remove = async (r) => {
    if (!window.confirm(`Eliminare l'utente ${r.email}?`)) return;
    try { await api.delete(`/users/${r.id}`); toast.success("Utente eliminato"); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div data-testid="users-page" className="space-y-3 max-w-4xl">
      <div className="flex items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-head font-bold tracking-tight">Utenti</h1>
          <p className="text-sm text-muted-foreground mt-1">Gestione accessi al gestionale</p>
        </div>
        <Button size="sm" className="rounded-sm font-semibold" onClick={() => setForm({ ...EMPTY })} data-testid="add-user-btn">
          <Plus size={15} className="mr-1.5" /> Nuovo utente
        </Button>
      </div>

      <div className="rounded-sm border border-border bg-card overflow-x-auto scroll-thin">
        <table className="w-full text-sm" data-testid="users-table">
          <thead className="bg-secondary">
            <tr>{["", "Nome", "Email", "Ruolo", "Stato"].map((h) => (
              <th key={h} className="px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{h}</th>
            ))}</tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-t border-border hover:bg-secondary/40" data-testid={`user-row-${r.email}`}>
                <td className="px-3 py-1.5">
                  <div className="flex justify-start gap-1">
                    <Button variant="ghost" size="icon" className="h-8 w-8 rounded-sm" title="Invia email di prova"
                      onClick={() => testEmail(r)} disabled={testing === r.id} data-testid={`test-email-${r.email}`}>
                      <PaperPlaneTilt size={15} />
                    </Button>
                    <Button variant="ghost" size="icon" className="h-8 w-8 rounded-sm"
                      onClick={() => setForm({ ...r, password: "" })} data-testid={`edit-user-${r.email}`}><PencilSimple size={15} /></Button>
                    {r.id !== user?.id && (
                      <Button variant="ghost" size="icon" className="h-8 w-8 rounded-sm text-destructive"
                        onClick={() => remove(r)} data-testid={`delete-user-${r.email}`}><Trash size={15} /></Button>
                    )}
                  </div>
                </td>
                <td className="px-3 py-1.5 font-semibold">{r.name}</td>
                <td className="px-3 py-1.5 text-muted-foreground">
                  {r.email}
                  {r.notify_email && <div className="text-xs">avvisi: {r.notify_email}</div>}
                  {(r.notify_email || r.email || "").endsWith("@azienda.it") && (
                    <div className="text-xs text-amber-600" data-testid={`user-demo-email-${r.email}`}>
                      indirizzo di prova: le email non vengono consegnate
                    </div>
                  )}
                </td>
                <td className="px-3 py-1.5">
                  <span className={`text-[10px] font-bold uppercase px-1.5 py-0.5 rounded-sm ${r.role === "admin"
                    ? "bg-primary text-primary-foreground" : "bg-secondary text-secondary-foreground border border-border"}`}>
                    {ROLE_LABEL[r.role] || r.role}
                  </span>
                </td>
                <td className="px-3 py-1.5 text-xs">{r.active === false ? "Disattivato" : "Attivo"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {form && (
        <Dialog open onOpenChange={() => setForm(null)}>
          <DialogContent className="rounded-sm sm:max-w-md" data-testid="user-dialog">
            <DialogHeader><DialogTitle className="font-head">{form.id ? "Modifica utente" : "Nuovo utente"}</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div>
                <Label className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Nome</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="h-9 rounded-sm mt-1" data-testid="user-form-name" />
              </div>
              <div>
                <Label className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Email</Label>
                <Input type="email" value={form.email} disabled={!!form.id}
                  onChange={(e) => setForm({ ...form, email: e.target.value })} className="h-9 rounded-sm mt-1" data-testid="user-form-email" />
              </div>
              <div>
                <Label className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Ruolo</Label>
                <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                  <SelectTrigger className="h-9 rounded-sm mt-1" data-testid="user-form-role"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="admin">Amministratore</SelectItem>
                    <SelectItem value="titolare">Titolare</SelectItem>
                    <SelectItem value="rspp">RSPP</SelectItem>
                    <SelectItem value="manager">Responsabile</SelectItem>
                    <SelectItem value="employee">Dipendente</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {(form.role === "titolare" || form.role === "manager") && (
                <div>
                  <Label className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                    Email per gli avvisi (riepilogo appello, archiviazioni, scadenze)
                  </Label>
                  <Input value={form.notify_email || ""} onChange={(e) => setForm({ ...form, notify_email: e.target.value })}
                    className="h-9 rounded-sm mt-1" placeholder="indirizzo email reale (le mail @azienda.it non vengono consegnate)"
                    data-testid="user-form-notify-email" />
                </div>
              )}
              {form.role === "employee" && (
                <div>
                  <Label className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Dipendente collegato</Label>
                  <Select value={form.employee_id || ""} onValueChange={(v) => setForm({ ...form, employee_id: v })}>
                    <SelectTrigger className="h-9 rounded-sm mt-1" data-testid="user-form-employee"><SelectValue placeholder="Seleziona dipendente" /></SelectTrigger>
                    <SelectContent className="max-h-72">
                      {employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.last_name} {e.first_name} ({e.code})</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div>
                <Label className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                  Password {form.id && "(lascia vuoto per non modificare)"}
                </Label>
                <Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
                  className="h-9 rounded-sm mt-1" data-testid="user-form-password" />
              </div>
            </div>
            <DialogFooter className="gap-2">
              <Button variant="outline" className="rounded-sm" onClick={() => setForm(null)} data-testid="user-cancel">Annulla</Button>
              <Button className="rounded-sm font-semibold" onClick={submit} data-testid="user-save">Salva</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
